import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Save, Check } from "lucide-react";

export default function Settings() {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        adminApi.get("/api/admin/settings").then((r) => setForm(r.data));
    }, []);

    if (!form) return <div className="p-10 text-stone-500">Loading…</div>;

    const update = (k, v) => setForm({ ...form, [k]: v });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { ...form };
        // Don't send the masked pwd
        if (payload.fundle_pwd && /^•+$/.test(payload.fundle_pwd)) {
            delete payload.fundle_pwd;
        }
        delete payload.fundle_pwd_set;
        await adminApi.put("/api/admin/settings", payload);
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const Field = ({ label, k, type = "text", placeholder, helper, textarea }) => (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                {label}
            </label>
            {textarea ? (
                <textarea
                    data-testid={`setting-${k}`}
                    rows={3}
                    value={form[k] ?? ""}
                    onChange={(e) => update(k, e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700 text-stone-900 text-sm"
                    placeholder={placeholder}
                />
            ) : (
                <input
                    data-testid={`setting-${k}`}
                    type={type}
                    value={form[k] ?? ""}
                    onChange={(e) =>
                        update(k, type === "number" ? Number(e.target.value) : e.target.value)
                    }
                    className="w-full px-3 py-2 rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700 text-stone-900 text-sm"
                    placeholder={placeholder}
                />
            )}
            {helper && <div className="text-xs text-stone-500 mt-1">{helper}</div>}
        </div>
    );

    return (
        <div className="p-10 max-w-4xl">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Configuration
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Settings</h1>
                <p className="text-stone-600 mt-2">
                    Fine-tune the Concierge greeting and connect to your rewards platform.
                </p>
            </div>

            <form onSubmit={save} className="space-y-10" data-testid="settings-form">
                {/* Bot tone */}
                <section className="bg-white border border-stone-200 rounded-lg p-6 space-y-5">
                    <h3 className="font-heading text-2xl text-orchid-700">Greeting & Tone</h3>
                    <Field
                        label="Welcome message"
                        k="welcome_message"
                        textarea
                        helper="Shown when a visitor first opens the chat."
                    />
                    <div className="grid grid-cols-2 gap-5">
                        <Field
                            label="Auto-greet delay (seconds)"
                            k="auto_greet_delay_seconds"
                            type="number"
                            helper="Toast preview appears after this delay."
                        />
                        <Field label="Business hours" k="business_hours" />
                    </div>
                </section>

                {/* Fundle */}
                <section className="bg-white border border-stone-200 rounded-lg p-6 space-y-5">
                    <h3 className="font-heading text-2xl text-orchid-700">
                        Fundle Loyalty API
                    </h3>
                    <p className="text-sm text-stone-600 -mt-3">
                        Connect to Fundle (loyaltyserstg.fundledata.com or production). Balance lookups use{" "}
                        <code className="font-mono text-xs bg-stone-100 px-1 rounded">GET_CUSTOMER_TRANS_INFO</code>;
                        redemptions use the two-step OTP flow via{" "}
                        <code className="font-mono text-xs bg-stone-100 px-1 rounded">GET_POINTS_VALIDATION_INFO</code> →{" "}
                        <code className="font-mono text-xs bg-stone-100 px-1 rounded">REDEEM_LOYALTY_POINTS_ACTION</code>.
                    </p>
                    <Field
                        label="Fundle base URL"
                        k="fundle_base_url"
                        placeholder="https://loyaltyserstg.fundledata.com/service.svc"
                        helper="Without trailing slash — endpoints are appended."
                    />
                    <div className="grid grid-cols-2 gap-5">
                        <Field
                            label="Fundle userid"
                            k="fundle_userid"
                            placeholder="fun_user"
                        />
                        <Field
                            label="Fundle pwd (API password)"
                            k="fundle_pwd"
                            type="password"
                            placeholder={form.fundle_pwd_set ? "(saved)" : "Paste pwd"}
                        />
                    </div>
                    <Field
                        label="Store code"
                        k="fundle_store_code"
                        placeholder="ONLINE"
                        helper="Sent as `store_code` in all Fundle requests."
                    />
                </section>

                {/* Support */}
                <section className="bg-white border border-stone-200 rounded-lg p-6 space-y-5">
                    <h3 className="font-heading text-2xl text-orchid-700">Support Contacts</h3>
                    <div className="grid grid-cols-2 gap-5">
                        <Field label="Primary phone" k="support_phone_primary" />
                        <Field label="Secondary phone" k="support_phone_secondary" />
                    </div>
                    <Field label="Support email" k="support_email" type="email" />
                </section>

                <div className="flex items-center gap-3 sticky bottom-0 bg-ivory/90 backdrop-blur p-3 -mx-3 rounded-md">
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="settings-save"
                        className="flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white font-medium px-6 py-2.5 rounded-md transition-colors disabled:opacity-60"
                    >
                        <Save size={15} /> {saving ? "Saving…" : "Save changes"}
                    </button>
                    {saved && (
                        <span className="text-sm text-orchid-500 flex items-center gap-1">
                            <Check size={15} /> Settings saved
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
}
